import {
    Block,
    CHILD_OBJECT_INVALID,
    CHILD_TYPE_INVALID,
    type Change,
    type NodeType,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
} from '@gitbook/slate';
import { assert } from '@gitbook/util-ts';

import { hasBlockConflict } from '../conflicts';
import { BLOCK_PARAGRAPH, BLOCK_STEPPER, BLOCK_STEPPER_STEP } from '../types';
import { unwrapChildren } from '../utils';

/*
 * Schema to validate stepper.
 */
export const schema = Schema.create({
    validations: [joinAdjacentSteppers],
    blocks: {
        [BLOCK_STEPPER]: {
            parent: { objects: ['document', 'block', 'fragment'] },
            nodes: [{ types: [BLOCK_STEPPER_STEP] }],
            isVoid: false,
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_TYPE_INVALID]: (change, context) => {
                    assert(Block.isBlock(context.node));

                    const stepper = context.node;

                    if (stepper.nodes.size === 1) {
                        return change.removeNodeByKey(stepper.key);
                    }

                    return change.wrapBlockByKey(
                        context.child.key,
                        Block.create({ type: BLOCK_STEPPER_STEP })
                    );
                },
            }),
        },
        [BLOCK_STEPPER_STEP]: {
            parent: { types: [BLOCK_STEPPER] },
            nodes: [{ objects: ['block'] }],
            isVoid: false,
            data: {
                icon: (icon) => icon === undefined || typeof icon === 'string',
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_OBJECT_INVALID]: (change, context) => {
                    if (context.child.object === 'text') {
                        return change.wrapBlockByKey(
                            context.child.key,
                            Block.create({
                                type: BLOCK_PARAGRAPH,
                            })
                        );
                    }

                    return;
                },
            }),
        },
    },
});

/**
 * A rule that joins adjacent steppers.
 */
function joinAdjacentSteppers(node: NodeType): undefined | ((change: Change) => void) {
    if (node.object !== 'document' && node.object !== 'block' && node.object !== 'fragment') {
        return undefined;
    }

    const invalids = node.nodes.reduce<[Block, Block][]>((pairs, child: unknown, i) => {
        if (!Block.isBlock(child) || child.type !== BLOCK_STEPPER || hasBlockConflict(child)) {
            return pairs;
        }

        const next = node.nodes.get(i + 1);
        if (
            !next ||
            !Block.isBlock(next) ||
            next.type !== BLOCK_STEPPER ||
            hasBlockConflict(next)
        ) {
            return pairs;
        }

        const lastPair = pairs[pairs.length - 1];
        if (lastPair?.[1] === child) {
            pairs.push([lastPair[0], next]);
        } else {
            pairs.push([child, next]);
        }

        return pairs;
    }, []);

    if (invalids.length === 0) {
        return undefined;
    }

    return (change) => {
        const changes: Array<[string, string, number] | string> = [];

        invalids.reverse().forEach(([first, second]) => {
            const updatedSecond = change.value.document.getDescendant(second.key);
            if (
                !updatedSecond ||
                !Block.isBlock(updatedSecond) ||
                updatedSecond.type !== BLOCK_STEPPER
            ) {
                return;
            }

            updatedSecond.nodes.forEach((secondNode, index) => {
                changes.push([secondNode.key, first.key, first.nodes.size + index]);
            });
            changes.push(second.key);
        });

        changes.forEach((entry) => {
            if (Array.isArray(entry)) {
                const [key, newKey, newIndex] = entry;
                change.moveNodeByKey(key, newKey, newIndex, { normalize: false });
            } else {
                change.removeNodeByKey(entry, { normalize: false });
            }
        });
    };
}
